import React, { useState, useRef, useEffect } from 'react';
import { Trash2, Loader2, Search } from 'lucide-react';
import { adminAPI, productAPI } from '../../../services/api';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { CustomerIntelligenceWidget } from './CustomerIntelligenceWidget';
import { EDIT_STATUS_OPTIONS, TRAFFIC_PLATFORM_OPTIONS, printPackingSlipHtml } from './orderModalConstants';

export interface EditOrderModalProps {
  isOpen: boolean;
  order: any;
  onClose: () => void;
  onOrderUpdated: (updatedOrder: any) => void;
  deliveryOptions: any[];
  siteSettings?: { site_name: string; support_phone: string; logo_url?: string };
}

export const EditOrderModal: React.FC<EditOrderModalProps> = ({
  isOpen,
  order,
  onClose,
  onOrderUpdated,
  deliveryOptions,
  siteSettings,
}) => {
  const isMobile = useIsMobile();
  const [editModalTab, setEditModalTab] = useState<'items' | 'slip' | 'fraud' | 'edit'>('items');
  const [editItems, setEditItems] = useState<any[]>([]);
  const [editProductSearch, setEditProductSearch] = useState('');
  const [editProductResults, setEditProductResults] = useState<any[]>([]);
  const [editProductSearching, setEditProductSearching] = useState(false);
  const [editPickingProduct, setEditPickingProduct] = useState<any>(null);
  const [editPickAttrs, setEditPickAttrs] = useState<{ color: string; size: string; weight: string; qty: number; price: number }>({
    color: '', size: '', weight: '', qty: 1, price: 0
  });
  const editSearchRef = useRef<any>(null);
  const [editForm, setEditForm] = useState({
    customer_name: '',
    customer_phone: '',
    shipping_address: '',
    notes: '',
    status: 'pending',
    shipping_cost: '60',
    delivery_option: null as number | null,
    traffic_source: 'manual',
    utm_campaign: '',
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string>('');

  // Admin discount state
  const [discountType, setDiscountType] = useState<'flat' | 'percentage' | ''>('');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [discountSaving, setDiscountSaving] = useState(false);
  const [discountError, setDiscountError] = useState<string>('');

  // Customer Steadfast History
  const [customerHistory, setCustomerHistory] = useState<any>(null);
  const [customerHistoryLoading, setCustomerHistoryLoading] = useState(false);
  const [customerHistoryError, setCustomerHistoryError] = useState<string>('');

  const packingSlipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && order) {
      let matchedOptionId = order.delivery_option || null;
      if (!matchedOptionId && deliveryOptions.length > 0) {
        const orderShipCost = parseFloat(order.shipping_cost || 0);
        const matchByCost = deliveryOptions.find(opt => parseFloat(opt.cost) === orderShipCost);
        if (matchByCost) {
          matchedOptionId = matchByCost.id;
        } else {
          matchedOptionId = deliveryOptions[0].id;
        }
      }

      setEditForm({
        customer_name: order.customer_name || '',
        customer_phone: order.customer_phone || '',
        shipping_address: order.shipping_address || '',
        notes: order.notes || '',
        status: order.status || 'pending',
        shipping_cost: order.shipping_cost !== undefined && order.shipping_cost !== null ? String(order.shipping_cost) : '60',
        delivery_option: matchedOptionId,
        traffic_source: order.traffic_source || (order.source === 'manual' ? 'manual' : 'organic'),
        utm_campaign: order.utm_campaign || '',
      });
      setEditError('');
      setCustomerHistory(null);
      setCustomerHistoryError('');

      // Clone order items for editing
      const clonedItems = (order.items || []).map((it: any) => ({
        id: it.id,
        product_id: it.product_id || it.product,
        product_name: it.product_name || 'Product',
        product_image: it.product_image || '',
        product_sku: it.product_sku || '',
        product_stock: it.product_stock !== undefined ? it.product_stock : 99,
        product_attributes: it.product_attributes || {},
        quantity: Number(it.quantity || 1),
        price: parseFloat(it.price || 0),
        subtotal: parseFloat(it.subtotal || (parseFloat(it.price || 0) * Number(it.quantity || 1))),
        selected_color: it.selected_color || '',
        selected_size: it.selected_size || '',
        selected_weight: it.selected_weight || '',
        selected_attributes: it.selected_attributes || {},
      }));
      setEditItems(clonedItems);
      setEditProductSearch('');
      setEditProductResults([]);
      setEditPickingProduct(null);
      setEditPickAttrs({ color: '', size: '', weight: '', qty: 1, price: 0 });
      setEditModalTab('items');

      setDiscountType((order.admin_discount_type as 'flat' | 'percentage' | '') || '');
      setDiscountValue(order.admin_discount_value ? String(order.admin_discount_value) : '');
      setDiscountError('');

      if (order.customer_phone) {
        loadCustomerHistory(order.customer_phone);
      }
    }
  }, [isOpen, order, deliveryOptions]);

  const loadCustomerHistory = async (phone: string) => {
    if (!phone) {
      setCustomerHistory(null);
      return;
    }
    setCustomerHistoryLoading(true);
    setCustomerHistoryError('');
    try {
      const res = await adminAPI.getCustomerSteadfastHistory(phone);
      setCustomerHistory(res.data || null);
    } catch (err: any) {
      const msg = err?.response?.data?.error || '';
      if (msg && !msg.includes('not configured')) {
        setCustomerHistoryError(msg);
      }
    } finally {
      setCustomerHistoryLoading(false);
    }
  };

  const handleEditProductSearch = (query: string) => {
    setEditProductSearch(query);
    if (editSearchRef.current) clearTimeout(editSearchRef.current);
    if (!query.trim()) { setEditProductResults([]); return; }
    editSearchRef.current = setTimeout(async () => {
      setEditProductSearching(true);
      try {
        const res = await productAPI.getProducts({ search: query, page: 1, limit: 10 } as any);
        const results = (res as any).results || res;
        setEditProductResults(Array.isArray(results) ? results : []);
      } catch { setEditProductResults([]); }
      finally { setEditProductSearching(false); }
    }, 350);
  };

  const handleSelectProductToEdit = (prod: any) => {
    setEditPickingProduct(prod);
    const pPrice = parseFloat(prod.discount_price || prod.price || 0);
    const firstColor = prod.attributes?.colors?.enabled && prod.attributes.colors.options?.[0]
      ? (typeof prod.attributes.colors.options[0] === 'string' ? prod.attributes.colors.options[0] : prod.attributes.colors.options[0]?.name || '')
      : '';
    const firstSize = prod.attributes?.sizes?.enabled && prod.attributes.sizes.options?.[0]
      ? (typeof prod.attributes.sizes.options[0] === 'string' ? prod.attributes.sizes.options[0] : prod.attributes.sizes.options[0]?.name || '')
      : '';
    const firstWeight = prod.attributes?.weights?.enabled && prod.attributes.weights.options?.[0]
      ? (typeof prod.attributes.weights.options[0] === 'string' ? prod.attributes.weights.options[0] : prod.attributes.weights.options[0]?.name || '')
      : '';

    setEditPickAttrs({
      color: firstColor,
      size: firstSize,
      weight: firstWeight,
      qty: 1,
      price: pPrice,
    });
  };

  const handleAddProductToEditItems = () => {
    if (!editPickingProduct) return;
    const prod = editPickingProduct;
    const unitPrice = parseFloat(String(editPickAttrs.price)) || parseFloat(prod.discount_price || prod.price || 0);
    const qty = Math.max(1, editPickAttrs.qty);

    const existingIdx = editItems.findIndex(
      it => it.product_id === prod.id &&
            (it.selected_color || '') === (editPickAttrs.color || '') &&
            (it.selected_size || '') === (editPickAttrs.size || '') &&
            (it.selected_weight || '') === (editPickAttrs.weight || '')
    );

    if (existingIdx >= 0) {
      setEditItems(prev => {
        const updated = [...prev];
        const newQty = updated[existingIdx].quantity + qty;
        updated[existingIdx] = {
          ...updated[existingIdx],
          quantity: newQty,
          subtotal: updated[existingIdx].price * newQty,
        };
        return updated;
      });
    } else {
      const newItem = {
        product_id: prod.id,
        product_name: prod.name_en || prod.name_bn || 'Product',
        product_image: prod.image || prod.images?.[0]?.image || '',
        product_sku: prod.sku || '',
        product_stock: prod.stock !== undefined ? prod.stock : 99,
        product_attributes: prod.attributes || {},
        quantity: qty,
        price: unitPrice,
        subtotal: unitPrice * qty,
        selected_color: editPickAttrs.color || '',
        selected_size: editPickAttrs.size || '',
        selected_weight: editPickAttrs.weight || '',
        selected_attributes: {},
      };
      setEditItems(prev => [...prev, newItem]);
    }

    setEditPickingProduct(null);
    setEditPickAttrs({ color: '', size: '', weight: '', qty: 1, price: 0 });
    setEditProductSearch('');
    setEditProductResults([]);
  };

  const handleUpdateItemQty = (index: number, newQty: number) => {
    if (newQty < 1) return;
    setEditItems(prev => {
      const copy = [...prev];
      const it = copy[index];
      if (!it) return prev;
      copy[index] = {
        ...it,
        quantity: newQty,
        subtotal: it.price * newQty,
      };
      return copy;
    });
  };

  const handleUpdateItemPrice = (index: number, newPrice: number) => {
    setEditItems(prev => {
      const copy = [...prev];
      const it = copy[index];
      if (!it) return prev;
      const validPrice = Math.max(0, isNaN(newPrice) ? 0 : newPrice);
      copy[index] = {
        ...it,
        price: validPrice,
        subtotal: validPrice * it.quantity,
      };
      return copy;
    });
  };

  const handleUpdateItemVariant = (index: number, field: 'selected_color' | 'selected_size' | 'selected_weight', value: string) => {
    setEditItems(prev => {
      const copy = [...prev];
      const it = copy[index];
      if (!it) return prev;
      copy[index] = {
        ...it,
        [field]: value,
      };
      return copy;
    });
  };

  const handleRemoveEditItem = (index: number) => {
    if (editItems.length <= 1) {
      alert('An order must have at least one product. To cancel the order, change the status to Cancelled.');
      return;
    }
    setEditItems(prev => prev.filter((_, i) => i !== index));
  };

  const handlePrintSlip = () => {
    if (packingSlipRef.current) {
      printPackingSlipHtml(packingSlipRef.current, order?.order_number);
    }
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleDeliveryOptionChange = (optionId: number | null) => {
    if (optionId === null) {
      setEditForm(prev => ({ ...prev, delivery_option: null }));
      return;
    }
    const found = deliveryOptions.find(opt => opt.id === optionId);
    if (found) {
      setEditForm(prev => ({
        ...prev,
        delivery_option: found.id,
        shipping_cost: String(found.cost),
      }));
    } else {
      setEditForm(prev => ({ ...prev, delivery_option: optionId }));
    }
  };

  const handleApplyDiscount = async (remove = false) => {
    if (!order) return;
    setDiscountSaving(true);
    setDiscountError('');
    try {
      const type = remove ? null : discountType || null;
      const value = remove ? null : (discountValue ? parseFloat(discountValue) : null);
      const res = await adminAPI.applyOrderDiscount(order.id, type, value);
      const updated = res.data;
      onOrderUpdated(updated);
      if (remove) {
        setDiscountType('');
        setDiscountValue('');
      }
    } catch (err: any) {
      setDiscountError(err?.response?.data?.error || err?.message || 'Failed to apply discount');
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!order) return;
    if (editItems.length === 0) {
      setEditError('Order must have at least one product.');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      const statusChanged = editForm.status !== order.status;
      const res = await adminAPI.updateOrder(order.id, {
        customer_name: editForm.customer_name,
        customer_phone: editForm.customer_phone,
        shipping_address: editForm.shipping_address,
        notes: editForm.notes,
        shipping_cost: parseFloat(editForm.shipping_cost) || 0,
        delivery_option: editForm.delivery_option || null,
        traffic_source: editForm.traffic_source,
        utm_campaign: editForm.utm_campaign.trim(),
        items: editItems.map(it => ({
          id: it.id,
          product_id: it.product_id,
          quantity: it.quantity,
          price: it.price,
          selected_color: it.selected_color || '',
          selected_size: it.selected_size || '',
          selected_weight: it.selected_weight || '',
          selected_attributes: it.selected_attributes || {},
        })),
      });
      let finalOrder = res.data;
      if (statusChanged) {
        const statusRes = await adminAPI.updateOrderStatus(order.id, editForm.status);
        finalOrder = { ...finalOrder, ...(statusRes.data || { status: editForm.status }) };
      }
      onOrderUpdated(finalOrder);
      onClose();
    } catch (error: any) {
      console.error('Failed to update order:', error);
      const data = error.response?.data;
      if (data && typeof data === 'object') {
        const msg = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
        setEditError(msg);
      } else {
        setEditError(error.response?.data?.error || error.message || 'Failed to save changes');
      }
    } finally {
      setEditSaving(false);
    }
  };

  if (!isOpen || !order) return null;

  const items: any[] = editItems;
  const itemsTotal = editItems.reduce((sum: number, it: any) => sum + (parseFloat(it.price || 0) * parseInt(it.quantity || 1)), 0);
  const shippingCost = parseFloat(editForm.shipping_cost) || 0;
  let discount = 0;
  if (discountType === 'percentage' && discountValue) {
    discount = Math.min((itemsTotal * parseFloat(discountValue)) / 100, itemsTotal);
  } else if (discountType === 'flat' && discountValue) {
    discount = Math.min(parseFloat(discountValue), itemsTotal);
  } else if (order.discount_amount) {
    discount = Math.min(parseFloat(order.discount_amount), itemsTotal);
  }
  const codAmount = Math.max(0, itemsTotal + shippingCost - discount);
  const orderDate = order.created_at
    ? new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.65)',
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: 'center',
              zIndex: 10000, backdropFilter: 'blur(4px)',
              padding: isMobile ? '0' : '16px'
            }}
            onClick={isMobile ? undefined : onClose}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: isMobile ? '0' : '14px',
                width: '100%',
                maxWidth: isMobile ? '100%' : '1180px',
                height: isMobile ? '100dvh' : 'auto',
                maxHeight: isMobile ? '100dvh' : '95vh',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* ── Modal Header ── */}
              <div style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #1e293b, #0f172a)',
                color: 'white',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexShrink: 0
              }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '600', color: '#f8fafc', letterSpacing: '-0.025em' }}>Edit Order</h2>
                  <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: '#94a3b8' }}>#{order.order_number}</p>
                </div>
                <button onClick={onClose} style={{
                  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px', color: 'white', fontSize: '1rem',
                  width: '28px', height: '28px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s'
                }}>✕</button>
              </div>

              {/* ── Mobile Tab Bar ── */}
              {isMobile && (
                <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', flexShrink: 0 }}>
                  {([
                    { key: 'items', label: `🛒 Items (${editItems.length})` },
                    { key: 'edit',  label: '✏️ Info'  },
                    { key: 'slip',  label: '🧾 Slip'  },
                    { key: 'fraud', label: '🛡️ Fraud' },
                  ] as { key: 'items' | 'edit' | 'slip' | 'fraud'; label: string }[]).map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setEditModalTab(tab.key)}
                      style={{
                        flex: 1, padding: '11px 6px',
                        border: 'none',
                        borderBottom: editModalTab === tab.key ? '2.5px solid #1a56db' : '2.5px solid transparent',
                        background: 'transparent',
                        color: editModalTab === tab.key ? '#1a56db' : '#6b7280',
                        fontWeight: editModalTab === tab.key ? '700' : '500',
                        fontSize: '13px', cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Body: Desktop = two-column | Mobile = tab-driven ── */}
              <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

              {/* ── LEFT: Edit Form ── */}
              {(!isMobile || editModalTab === 'edit') && (
                <div style={{
                  width: isMobile ? '100%' : '350px',
                  minWidth: isMobile ? undefined : '320px',
                  flexShrink: 0,
                  padding: '16px 18px', overflowY: 'auto',
                  borderRight: isMobile ? 'none' : '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc'
                }}>
                  {/* ── Move to Category Dropdown ── */}
                  <div style={{ marginBottom: '14px', background: '#fff', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2.5px solid #3b82f6',
                        paddingBottom: '3px',
                        display: 'inline-block'
                      }}>
                        MOVE TO CATEGORY
                      </span>
                      {(() => {
                        const cur = EDIT_STATUS_OPTIONS.find(st => st.value === editForm.status) || {
                          label: editForm.status,
                          color: '#475569',
                          bg: '#f1f5f9',
                          border: '#cbd5e1'
                        };
                        return (
                          <span style={{
                            fontSize: '0.72rem',
                            fontWeight: '700',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            background: cur.bg,
                            color: cur.color,
                            border: `1px solid ${cur.border}`
                          }}>
                            ● {cur.label}
                          </span>
                        );
                      })()}
                    </div>

                    <select
                      name="status"
                      value={editForm.status}
                      onChange={handleEditFormChange}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '7px',
                        fontSize: '0.88rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    >
                      {EDIT_STATUS_OPTIONS.map(st => (
                        <option key={st.value} value={st.value}>
                          {st.icon} {st.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Order Platform / Source Dropdown */}
                  <div style={{ marginBottom: '14px', background: '#fff', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2.5px solid #8b5cf6',
                        paddingBottom: '3px',
                        display: 'inline-block'
                      }}>
                        ORDER PLATFORM / SOURCE
                      </span>
                      {(() => {
                        const cur = TRAFFIC_PLATFORM_OPTIONS.find(p => p.value === editForm.traffic_source) || {
                          label: editForm.traffic_source || 'Manual',
                          color: '#475569',
                          bg: '#f1f5f9',
                          border: '#cbd5e1'
                        };
                        return (
                          <span style={{
                            fontSize: '0.72rem',
                            fontWeight: '700',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            background: cur.bg,
                            color: cur.color,
                            border: `1px solid ${cur.border}`
                          }}>
                            ● {cur.label}
                          </span>
                        );
                      })()}
                    </div>

                    <select
                      name="traffic_source"
                      value={editForm.traffic_source}
                      onChange={handleEditFormChange}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '7px',
                        fontSize: '0.88rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer',
                        outline: 'none',
                        boxSizing: 'border-box',
                        marginBottom: '8px'
                      }}
                    >
                      {TRAFFIC_PLATFORM_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.icon} {opt.label}
                        </option>
                      ))}
                    </select>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '3px' }}>
                        Campaign / Ad Name (Optional)
                      </label>
                      <input
                        type="text"
                        name="utm_campaign"
                        value={editForm.utm_campaign}
                        onChange={handleEditFormChange}
                        placeholder="e.g. Eid Campaign, FB Ad 1, Post 2..."
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '0.82rem',
                          color: '#1e293b',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  <p style={{ margin: '4px 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '4px', display: 'inline-block' }}>Order Details</p>

                  {editError && (
                    <div style={{
                      background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5',
                      borderRadius: '8px', padding: '9px 12px', marginBottom: '14px', fontSize: '12px'
                    }}>⚠️ {editError}</div>
                  )}

                  {/* Customer Name */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Customer Name</label>
                    <input type="text" name="customer_name" value={editForm.customer_name} onChange={handleEditFormChange}
                      placeholder="Customer full name"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Phone */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Phone Number</label>
                    <input type="text" name="customer_phone" value={editForm.customer_phone} onChange={handleEditFormChange}
                      placeholder="Customer phone"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Shipping Address */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Shipping Address</label>
                    <textarea name="shipping_address" value={editForm.shipping_address} onChange={handleEditFormChange}
                      placeholder="Full shipping address" rows={3}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>

                  {/* ── Delivery Option & Shipping Fee ── */}
                  <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>
                      🚚 Delivery Option &amp; Shipping Fee
                    </p>
                    
                    {/* Delivery Option Dropdown */}
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>
                        Shipping Option
                      </label>
                      <select
                        value={editForm.delivery_option || (deliveryOptions[0]?.id || '')}
                        onChange={(e) => {
                          const optId = e.target.value ? parseInt(e.target.value) : null;
                          handleDeliveryOptionChange(optId);
                        }}
                        style={{
                          width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
                          borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', fontWeight: '600',
                          backgroundColor: '#f8fafc', cursor: 'pointer', outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      >
                        {deliveryOptions.map(opt => (
                          <option key={opt.id} value={opt.id}>
                            {opt.icon || '🚚'} {opt.name_en} — ৳{parseFloat(opt.cost).toFixed(0)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Custom Shipping Fee Input */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '3px' }}>
                        Shipping Cost (৳)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        name="shipping_cost"
                        value={editForm.shipping_cost}
                        onChange={(e) => setEditForm(prev => ({ ...prev, shipping_cost: e.target.value }))}
                        placeholder="Shipping fee in ৳"
                        style={{
                          width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1',
                          borderRadius: '7px', fontSize: '0.85rem', fontWeight: '600',
                          color: '#1e293b', outline: 'none', boxSizing: 'border-box',
                          backgroundColor: '#fff'
                        }}
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div style={{ marginBottom: '6px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Notes</label>
                    <textarea name="notes" value={editForm.notes} onChange={handleEditFormChange}
                      placeholder="Optional order notes" rows={2}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>

                  {/* ── Admin Discount ── */}
                  <div style={{ marginTop: '14px', padding: '12px 14px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>
                      Admin Discount
                    </p>

                    {/* Current discount badge */}
                    {order.discount_amount && parseFloat(order.discount_amount) > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', padding: '7px 10px', background: '#d1fae5', borderRadius: '8px', border: '1px solid #6ee7b7' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#065f46' }}>
                          {order.admin_discount_type === 'percentage'
                            ? `−${order.admin_discount_value}% = ৳${parseFloat(order.discount_amount).toFixed(2)}`
                            : `−৳${parseFloat(order.discount_amount).toFixed(2)} (flat)`
                          }
                        </span>
                        <button
                          onClick={() => handleApplyDiscount(true)}
                          disabled={discountSaving}
                          title="Remove discount"
                          style={{ border: 'none', background: '#fca5a5', color: '#991b1b', borderRadius: '6px', padding: '3px 8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                        >✕ Remove</button>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
                      {/* Type selector */}
                      <div style={{ flex: '0 0 auto' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Type</label>
                        <select
                          value={discountType}
                          onChange={(e) => { setDiscountType(e.target.value as 'flat' | 'percentage' | ''); setDiscountError(''); }}
                          style={{ padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '12px', color: '#111827', background: 'white', cursor: 'pointer', outline: 'none' }}
                        >
                          <option value="">— Type —</option>
                          <option value="flat">৳ Flat</option>
                          <option value="percentage">% Percent</option>
                        </select>
                      </div>

                      {/* Value input */}
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                          Value {discountType === 'percentage' ? '(%)' : discountType === 'flat' ? '(৳)' : ''}
                        </label>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={discountValue}
                          onChange={(e) => { setDiscountValue(e.target.value); setDiscountError(''); }}
                          placeholder={discountType === 'percentage' ? 'e.g. 10' : discountType === 'flat' ? 'e.g. 150' : ''}
                          style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '13px', color: '#111827', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Apply button */}
                      <button
                        onClick={() => handleApplyDiscount(false)}
                        disabled={discountSaving || !discountType || !discountValue}
                        style={{
                          padding: '7px 12px',
                          border: 'none',
                          borderRadius: '7px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: discountSaving || !discountType || !discountValue ? 'not-allowed' : 'pointer',
                          background: discountSaving || !discountType || !discountValue ? '#e5e7eb' : 'linear-gradient(135deg, #16a34a, #15803d)',
                          color: discountSaving || !discountType || !discountValue ? '#9ca3af' : '#fff',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.15s',
                          alignSelf: 'flex-end',
                          boxSizing: 'border-box',
                          height: '34px',
                        }}
                      >
                        {discountSaving ? '⏳' : '✓ Apply'}
                      </button>
                    </div>

                    {discountError && (
                      <div style={{ marginTop: '8px', padding: '6px 10px', background: '#fee2e2', color: '#b91c1c', borderRadius: '6px', fontSize: '11px' }}>
                        ⚠️ {discountError}
                      </div>
                    )}

                    {/* Live preview */}
                    {(() => {
                      const items = order.items || [];
                      const itemsSubtotal = items.reduce((s: number, i: any) => s + parseFloat(i.subtotal || 0), 0);
                      const shipping = parseFloat(editForm.shipping_cost) || 0;
                      const currentDiscount = parseFloat(order.discount_amount || 0);
                      const finalAmt = itemsSubtotal + shipping - currentDiscount;
                      return (
                        <div style={{ marginTop: '10px', padding: '8px 10px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', fontSize: '11px', color: '#374151' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}><span>Items Subtotal</span><span style={{ fontWeight: '600' }}>৳{itemsSubtotal.toFixed(2)}</span></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}><span>Shipping</span><span style={{ fontWeight: '600' }}>৳{shipping.toFixed(2)}</span></div>
                          {currentDiscount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', color: '#16a34a' }}><span>Discount</span><span style={{ fontWeight: '700' }}>−৳{currentDiscount.toFixed(2)}</span></div>}
                          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #bbf7d0', paddingTop: '4px', marginTop: '2px' }}><span style={{ fontWeight: '800', color: '#065f46' }}>COD (Final)</span><span style={{ fontWeight: '800', color: '#065f46', fontSize: '12px' }}>৳{finalAmt.toFixed(2)}</span></div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )} {/* end LEFT panel */}

              {/* ── RIGHT: Products & Items, Packing Slip, & Fraud ── */}
              {(!isMobile || editModalTab !== 'edit') && (
                <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px' : '20px', backgroundColor: '#fff' }}>
                  {/* Desktop Right Column Tabs */}
                  {!isMobile && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid #e2e8f0',
                      paddingBottom: '12px',
                      marginBottom: '16px',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          onClick={() => setEditModalTab('items')}
                          style={{
                            padding: '7px 14px',
                            borderRadius: '8px',
                            border: editModalTab === 'items' ? '1px solid #bfdbfe' : '1px solid transparent',
                            background: editModalTab === 'items' ? '#eff6ff' : '#f8fafc',
                            color: editModalTab === 'items' ? '#1d4ed8' : '#64748b',
                            fontWeight: editModalTab === 'items' ? '700' : '600',
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.15s'
                          }}
                        >
                          🛒 Products &amp; Items
                          <span style={{
                            background: editModalTab === 'items' ? '#2563eb' : '#cbd5e1',
                            color: editModalTab === 'items' ? '#fff' : '#475569',
                            borderRadius: '10px',
                            padding: '1px 7px',
                            fontSize: '11px',
                            fontWeight: '700'
                          }}>{editItems.length}</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setEditModalTab('slip')}
                          style={{
                            padding: '7px 14px',
                            borderRadius: '8px',
                            border: editModalTab === 'slip' ? '1px solid #bfdbfe' : '1px solid transparent',
                            background: editModalTab === 'slip' ? '#eff6ff' : '#f8fafc',
                            color: editModalTab === 'slip' ? '#1d4ed8' : '#64748b',
                            fontWeight: editModalTab === 'slip' ? '700' : '600',
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.15s'
                          }}
                        >
                          🧾 Steadfast Packing Slip
                        </button>

                        <button
                          type="button"
                          onClick={() => setEditModalTab('fraud')}
                          style={{
                            padding: '7px 14px',
                            borderRadius: '8px',
                            border: editModalTab === 'fraud' ? '1px solid #bfdbfe' : '1px solid transparent',
                            background: editModalTab === 'fraud' ? '#eff6ff' : '#f8fafc',
                            color: editModalTab === 'fraud' ? '#1d4ed8' : '#64748b',
                            fontWeight: editModalTab === 'fraud' ? '700' : '600',
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.15s'
                          }}
                        >
                          🛡️ Customer History &amp; Fraud
                        </button>
                      </div>

                      {editModalTab === 'slip' && (
                        <button type="button" onClick={handlePrintSlip} style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '7px 14px', backgroundColor: '#0f172a', color: 'white',
                          border: 'none', borderRadius: '7px', fontSize: '12px',
                          fontWeight: '600', cursor: 'pointer'
                        }}>🖨️ Print Slip</button>
                      )}
                    </div>
                  )}

                  {/* ── TAB 1: Products & Variants Editor ── */}
                  {editModalTab === 'items' && (
                    <div className="om-edit-items-container">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>
                            Order Items &amp; Variants
                          </h4>
                          <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                            Modify variants, adjust quantities, change prices, or add additional products
                          </p>
                        </div>
                        <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '20px', padding: '3px 10px' }}>
                          {editItems.length} {editItems.length === 1 ? 'Item' : 'Items'}
                        </div>
                      </div>

                      {/* Items List */}
                      {editItems.length === 0 ? (
                        <div style={{ padding: '30px', textAlign: 'center', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
                          <p style={{ fontSize: '0.9rem', fontWeight: '600', margin: '0 0 6px' }}>No products in this order</p>
                          <p style={{ fontSize: '0.78rem', margin: 0 }}>Use the search box below to add products.</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {editItems.map((item, idx) => {
                            const attrs = item.product_attributes || {};
                            const colorOpts: any[] = attrs.colors?.options || [];
                            const sizeOpts: any[] = attrs.sizes?.options || [];
                            const weightOpts: any[] = attrs.weights?.options || [];
                            const stock = item.product_stock !== undefined ? item.product_stock : 99;
                            const subtotal = (parseFloat(item.price || 0) * parseInt(item.quantity || 1));

                            return (
                              <div key={item.id || `new-item-${idx}`} className="om-item-card">
                                <div className="om-item-top">
                                  <div className="om-item-info">
                                    {item.product_image ? (
                                      <img src={item.product_image} alt="" className="om-item-thumb" />
                                    ) : (
                                      <div className="om-item-thumb" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                        📦
                                      </div>
                                    )}
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <div className="om-item-name" title={item.product_name}>
                                        {item.product_name}
                                      </div>
                                      <div className="om-item-meta">
                                        {item.product_sku && (
                                          <span className="om-item-sku">SKU: {item.product_sku}</span>
                                        )}
                                        <span className={`om-item-stock ${stock > 5 ? 'in-stock' : stock > 0 ? 'low-stock' : 'out-stock'}`}>
                                          ● {stock > 0 ? `${stock} in stock` : 'Out of stock'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => handleRemoveEditItem(idx)}
                                    className="om-remove-btn"
                                    title="Remove this product"
                                    disabled={editItems.length <= 1}
                                    style={{ opacity: editItems.length <= 1 ? 0.4 : 1, cursor: editItems.length <= 1 ? 'not-allowed' : 'pointer' }}
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </div>

                                {/* Variants Row */}
                                <div className="om-item-variants-row">
                                  {/* Color */}
                                  <div className="om-variant-field">
                                    <span className="om-variant-label">Color:</span>
                                    {colorOpts.length > 0 ? (
                                      <select
                                        className="om-variant-select"
                                        value={item.selected_color || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_color', e.target.value)}
                                      >
                                        <option value="">— None —</option>
                                        {item.selected_color && !colorOpts.some((c: any) => (typeof c === 'string' ? c : c.name) === item.selected_color) && (
                                          <option value={item.selected_color}>{item.selected_color} (current)</option>
                                        )}
                                        {colorOpts.map((c: any, cIdx: number) => {
                                          const name = typeof c === 'string' ? c : c.name;
                                          const extra = typeof c === 'object' && c.extra_price ? ` (+৳${c.extra_price})` : '';
                                          return <option key={cIdx} value={name}>{name}{extra}</option>;
                                        })}
                                      </select>
                                    ) : (
                                      <input
                                        type="text"
                                        placeholder="e.g. Red, Black"
                                        value={item.selected_color || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_color', e.target.value)}
                                        style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem', width: '90px' }}
                                      />
                                    )}
                                  </div>

                                  {/* Size */}
                                  <div className="om-variant-field">
                                    <span className="om-variant-label">Size:</span>
                                    {sizeOpts.length > 0 ? (
                                      <select
                                        className="om-variant-select"
                                        value={item.selected_size || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_size', e.target.value)}
                                      >
                                        <option value="">— None —</option>
                                        {item.selected_size && !sizeOpts.some((s: any) => (typeof s === 'string' ? s : s.name) === item.selected_size) && (
                                          <option value={item.selected_size}>{item.selected_size} (current)</option>
                                        )}
                                        {sizeOpts.map((s: any, sIdx: number) => {
                                          const name = typeof s === 'string' ? s : s.name;
                                          const extra = typeof s === 'object' && s.extra_price ? ` (+৳${s.extra_price})` : '';
                                          return <option key={sIdx} value={name}>{name}{extra}</option>;
                                        })}
                                      </select>
                                    ) : (
                                      <input
                                        type="text"
                                        placeholder="e.g. M, L, XL"
                                        value={item.selected_size || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_size', e.target.value)}
                                        style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem', width: '80px' }}
                                      />
                                    )}
                                  </div>

                                  {/* Weight */}
                                  <div className="om-variant-field">
                                    <span className="om-variant-label">Weight:</span>
                                    {weightOpts.length > 0 ? (
                                      <select
                                        className="om-variant-select"
                                        value={item.selected_weight || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_weight', e.target.value)}
                                      >
                                        <option value="">— None —</option>
                                        {item.selected_weight && !weightOpts.some((w: any) => (typeof w === 'string' ? w : w.name) === item.selected_weight) && (
                                          <option value={item.selected_weight}>{item.selected_weight} (current)</option>
                                        )}
                                        {weightOpts.map((w: any, wIdx: number) => {
                                          const name = typeof w === 'string' ? w : w.name;
                                          const extra = typeof w === 'object' && w.extra_price ? ` (+৳${w.extra_price})` : '';
                                          return <option key={wIdx} value={name}>{name}{extra}</option>;
                                        })}
                                      </select>
                                    ) : (
                                      <input
                                        type="text"
                                        placeholder="e.g. 500g, 1kg"
                                        value={item.selected_weight || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_weight', e.target.value)}
                                        style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem', width: '80px' }}
                                      />
                                    )}
                                  </div>
                                </div>

                                {/* Controls: Qty, Unit Price, Subtotal */}
                                <div className="om-item-controls-row">
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '0.78rem', fontWeight: '600', color: '#64748b' }}>Quantity:</span>
                                    <div className="om-qty-stepper">
                                      <button
                                        type="button"
                                        className="om-qty-btn"
                                        onClick={() => handleUpdateItemQty(idx, Math.max(1, item.quantity - 1))}
                                      >
                                        −
                                      </button>
                                      <input
                                        type="number"
                                        min="1"
                                        className="om-qty-input"
                                        value={item.quantity}
                                        onChange={(e) => handleUpdateItemQty(idx, parseInt(e.target.value) || 1)}
                                      />
                                      <button
                                        type="button"
                                        className="om-qty-btn"
                                        onClick={() => handleUpdateItemQty(idx, item.quantity + 1)}
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>

                                  <div className="om-price-field">
                                    <span>Unit Price: ৳</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      className="om-price-input"
                                      value={item.price}
                                      onChange={(e) => handleUpdateItemPrice(idx, parseFloat(e.target.value) || 0)}
                                    />
                                  </div>

                                  <div style={{ textAlign: 'right' }}>
                                    <span style={{ fontSize: '0.72rem', color: '#64748b', display: 'block' }}>Subtotal</span>
                                    <span className="om-item-subtotal-badge">৳{subtotal.toFixed(2)}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Add Product Box */}
                      <div className="om-add-product-box">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            ➕ Add Additional Product to Order
                          </span>
                          {editProductSearching && (
                            <span style={{ fontSize: '0.75rem', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Loader2 size={13} className="animate-spin" /> Searching catalog...
                            </span>
                          )}
                        </div>

                        <div style={{ position: 'relative' }}>
                          <div style={{ display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0 10px' }}>
                            <Search size={15} style={{ color: '#94a3b8', marginRight: '6px' }} />
                            <input
                              type="text"
                              placeholder="Type product name or SKU to search catalog..."
                              value={editProductSearch}
                              onChange={(e) => handleEditProductSearch(e.target.value)}
                              style={{ width: '100%', padding: '8px 0', border: 'none', outline: 'none', fontSize: '0.84rem', color: '#1e293b' }}
                            />
                            {editProductSearch && (
                              <button
                                type="button"
                                onClick={() => { setEditProductSearch(''); setEditProductResults([]); }}
                                style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '12px' }}
                              >
                                ✕
                              </button>
                            )}
                          </div>

                          {/* Search Results Dropdown */}
                          {editProductResults.length > 0 && !editPickingProduct && (
                            <div className="om-search-results-dropdown">
                              {editProductResults.map((p) => {
                                const pPrice = parseFloat(p.discount_price || p.price || 0);
                                const pImg = p.image || p.images?.[0]?.image || '';
                                const attrs = p.attributes || {};
                                const colorOpts: any[] = (attrs.colors?.enabled && attrs.colors?.options?.length) ? attrs.colors.options : [];
                                const sizeOpts: any[] = (attrs.sizes?.enabled && attrs.sizes?.options?.length) ? attrs.sizes.options : [];
                                const weightOpts: any[] = (attrs.weights?.enabled && attrs.weights?.options?.length) ? attrs.weights.options : [];

                                return (
                                  <div
                                    key={p.id}
                                    className="om-search-result-item"
                                    onClick={() => handleSelectProductToEdit(p)}
                                  >
                                    {pImg ? (
                                      <img
                                        src={pImg}
                                        alt=""
                                        className="om-search-thumb"
                                        style={{
                                          width: '44px',
                                          height: '44px',
                                          minWidth: '44px',
                                          minHeight: '44px',
                                          maxWidth: '44px',
                                          maxHeight: '44px',
                                          objectFit: 'cover',
                                          borderRadius: '6px',
                                          border: '1px solid #e2e8f0',
                                          flexShrink: 0
                                        }}
                                      />
                                    ) : (
                                      <div
                                        className="om-search-thumb-placeholder"
                                        style={{
                                          width: '44px',
                                          height: '44px',
                                          minWidth: '44px',
                                          borderRadius: '6px',
                                          background: '#f1f5f9',
                                          border: '1px solid #e2e8f0',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '18px',
                                          flexShrink: 0
                                        }}
                                      >
                                        📦
                                      </div>
                                    )}

                                    <div className="om-search-info">
                                      <div className="om-search-name" title={p.name_en || p.name_bn}>
                                        {p.name_en || p.name_bn}
                                      </div>
                                      <div className="om-search-sub">
                                        {p.sku && <span>SKU: {p.sku}</span>}
                                        <span style={{ color: p.stock > 0 ? '#16a34a' : '#dc2626', fontWeight: '600' }}>
                                          ● {p.stock > 0 ? `${p.stock} in stock` : 'Out of stock'}
                                        </span>
                                      </div>

                                      {/* Available Variants Row */}
                                      {(colorOpts.length > 0 || sizeOpts.length > 0 || weightOpts.length > 0) && (
                                        <div className="om-search-variants-pills">
                                          {colorOpts.length > 0 && (
                                            <span className="om-search-variant-pill" title={colorOpts.map((c: any) => typeof c === 'string' ? c : c.name).join(', ')}>
                                              🎨 {colorOpts.map((c: any) => typeof c === 'string' ? c : c.name).slice(0, 3).join(', ')}{colorOpts.length > 3 ? ` +${colorOpts.length - 3}` : ''}
                                            </span>
                                          )}
                                          {sizeOpts.length > 0 && (
                                            <span className="om-search-variant-pill" title={sizeOpts.map((s: any) => typeof s === 'string' ? s : s.name).join(', ')}>
                                              📏 {sizeOpts.map((s: any) => typeof s === 'string' ? s : s.name).slice(0, 3).join(', ')}{sizeOpts.length > 3 ? ` +${sizeOpts.length - 3}` : ''}
                                            </span>
                                          )}
                                          {weightOpts.length > 0 && (
                                            <span className="om-search-variant-pill" title={weightOpts.map((w: any) => typeof w === 'string' ? w : w.name).join(', ')}>
                                              ⚖️ {weightOpts.map((w: any) => typeof w === 'string' ? w : w.name).slice(0, 2).join(', ')}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    <div className="om-search-price-col">
                                      <div className="om-search-price">৳{pPrice.toFixed(2)}</div>
                                      <span className="om-search-select-btn">+ Select</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Picking Product Variant Selector */}
                        {editPickingProduct && (
                          <div style={{ marginTop: '10px', background: 'white', border: '1px solid #93c5fd', borderRadius: '8px', padding: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                              {editPickingProduct.image && (
                                <img src={editPickingProduct.image} alt="" style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0' }} />
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b' }}>{editPickingProduct.name_en}</div>
                                <div style={{ fontSize: '0.75rem', color: '#2563eb', fontWeight: '600' }}>
                                  Catalog Price: ৳{parseFloat(editPickingProduct.discount_price || editPickingProduct.price).toFixed(2)}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setEditPickingProduct(null)}
                                style={{ border: 'none', background: '#f1f5f9', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: '#64748b', cursor: 'pointer' }}
                              >
                                ✕ Cancel
                              </button>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                              {/* Color */}
                              {editPickingProduct.attributes?.colors?.enabled && (
                                <div>
                                  <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Color</label>
                                  <select
                                    value={editPickAttrs.color}
                                    onChange={(e) => setEditPickAttrs(p => ({ ...p, color: e.target.value }))}
                                    style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                  >
                                    <option value="">— None —</option>
                                    {(editPickingProduct.attributes.colors.options || []).map((c: any, cIdx: number) => {
                                      const name = typeof c === 'string' ? c : c.name;
                                      return <option key={cIdx} value={name}>{name}</option>;
                                    })}
                                  </select>
                                </div>
                              )}

                              {/* Size */}
                              {editPickingProduct.attributes?.sizes?.enabled && (
                                <div>
                                  <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Size</label>
                                  <select
                                    value={editPickAttrs.size}
                                    onChange={(e) => setEditPickAttrs(p => ({ ...p, size: e.target.value }))}
                                    style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                  >
                                    <option value="">— None —</option>
                                    {(editPickingProduct.attributes.sizes.options || []).map((s: any, sIdx: number) => {
                                      const name = typeof s === 'string' ? s : s.name;
                                      return <option key={sIdx} value={name}>{name}</option>;
                                    })}
                                  </select>
                                </div>
                              )}

                              {/* Weight */}
                              {editPickingProduct.attributes?.weights?.enabled && (
                                <div>
                                  <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Weight</label>
                                  <select
                                    value={editPickAttrs.weight}
                                    onChange={(e) => setEditPickAttrs(p => ({ ...p, weight: e.target.value }))}
                                    style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                  >
                                    <option value="">— None —</option>
                                    {(editPickingProduct.attributes.weights.options || []).map((w: any, wIdx: number) => {
                                      const name = typeof w === 'string' ? w : w.name;
                                      return <option key={wIdx} value={name}>{name}</option>;
                                    })}
                                  </select>
                                </div>
                              )}

                              {/* Quantity */}
                              <div>
                                <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Quantity</label>
                                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', width: 'fit-content' }}>
                                  <button
                                    type="button"
                                    onClick={() => setEditPickAttrs(p => ({ ...p, qty: Math.max(1, p.qty - 1) }))}
                                    style={{ width: '26px', height: '26px', border: 'none', background: '#f8fafc', cursor: 'pointer', fontWeight: '700' }}
                                  >
                                    −
                                  </button>
                                  <span style={{ minWidth: '28px', textAlign: 'center', fontSize: '0.85rem', fontWeight: '700' }}>{editPickAttrs.qty}</span>
                                  <button
                                    type="button"
                                    onClick={() => setEditPickAttrs(p => ({ ...p, qty: p.qty + 1 }))}
                                    style={{ width: '26px', height: '26px', border: 'none', background: '#f8fafc', cursor: 'pointer', fontWeight: '700' }}
                                  >
                                    +
                                  </button>
                                </div>
                              </div>

                              {/* Custom Unit Price */}
                              <div>
                                <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Unit Price (৳)</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={editPickAttrs.price}
                                  onChange={(e) => setEditPickAttrs(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))}
                                  style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', fontWeight: '700' }}
                                />
                              </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={handleAddProductToEditItems}
                                style={{
                                  padding: '8px 18px',
                                  borderRadius: '7px',
                                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                                  color: 'white',
                                  border: 'none',
                                  fontSize: '0.82rem',
                                  fontWeight: '700',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)'
                                }}
                              >
                                ✓ Add to Order
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* ── Compact Dynamic Invoice Breakdown ── */}
                      {(() => {
                        const editTotalUnits = editItems.reduce((acc, it) => acc + (parseInt(it.quantity || it.qty || 1) || 1), 0);
                        const curDelOpt = deliveryOptions.find(o => String(o.id) === String(editForm.delivery_option));

                        return (
                          <div className="om-invoice-summary-card">
                            <div className="om-invoice-header">
                              <div className="om-invoice-title">
                                🧾 Order Invoice Breakdown
                              </div>
                              <span className="om-invoice-badge-pill">
                                COD Balance Due
                              </span>
                            </div>

                            <div className="om-invoice-body">
                              <div className="om-invoice-row">
                                <span className="om-invoice-label">
                                  Items Subtotal
                                  <span className="om-invoice-sublabel">
                                    ({editItems.length} {editItems.length === 1 ? 'product' : 'products'} · {editTotalUnits} {editTotalUnits === 1 ? 'unit' : 'units'})
                                  </span>
                                </span>
                                <span className="om-invoice-val">
                                  ৳{itemsTotal.toFixed(2)}
                                </span>
                              </div>

                              <div className="om-invoice-row">
                                <span className="om-invoice-label">
                                  Delivery Fee
                                  {curDelOpt && (
                                    <span className="om-invoice-tag">
                                      🚚 {curDelOpt.name_en || curDelOpt.name_bn || 'Selected'}
                                    </span>
                                  )}
                                </span>
                                <span className="om-invoice-val">
                                  +৳{shippingCost.toFixed(2)}
                                </span>
                              </div>

                              {discount > 0 && (
                                <div className="om-invoice-row discount">
                                  <span className="om-invoice-label">
                                    Admin Discount
                                    <span className="om-invoice-sublabel">
                                      ({order?.admin_discount_type === 'percentage' ? `${order?.admin_discount_value}% off` : 'Flat deduction'})
                                    </span>
                                  </span>
                                  <span className="om-invoice-val">
                                    −৳{discount.toFixed(2)}
                                  </span>
                                </div>
                              )}

                              <div className="om-invoice-total-row">
                                <div className="om-invoice-total-left">
                                  <span className="om-invoice-total-title">Total (COD Amount)</span>
                                  <span className="om-invoice-total-desc">Payable by customer upon delivery</span>
                                </div>
                                <div className="om-invoice-total-amount">
                                  ৳{codAmount.toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* ── TAB 2: Steadfast Packing Slip ── */}
                  {editModalTab === 'slip' && (
                    <div>
                      {isMobile && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>Packing Slip</span>
                          <button type="button" onClick={handlePrintSlip} style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '6px 12px', backgroundColor: '#0f172a', color: 'white',
                            border: 'none', borderRadius: '6px', fontSize: '12px',
                            fontWeight: '600', cursor: 'pointer'
                          }}>🖨️ Print</button>
                        </div>
                      )}

                      {/* ── Slip Content (ref for print) ── */}
                      <div ref={packingSlipRef} style={{
                    border: '1px solid #d1d5db', borderRadius: '8px',
                    overflow: 'hidden', fontSize: '13px', color: '#111', fontFamily: 'Arial, sans-serif'
                  }}>

                    {/* Slip Header */}
                    <div style={{
                      padding: '14px 18px',
                      background: '#111827',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {siteSettings?.logo_url && (
                          <img src={siteSettings.logo_url} alt="logo" style={{ height: '36px', objectFit: 'contain', borderRadius: '4px' }} />
                        )}
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '15px', color: '#fff' }}>{siteSettings?.site_name || 'Store'}</div>
                          {siteSettings?.support_phone && (
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>📞 {siteSettings.support_phone}</div>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Packing Slip</div>
                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#fff', marginTop: '2px' }}>STEADFAST COURIER</div>
                      </div>
                    </div>

                    {/* Invoice + Recipient row */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
                      {/* Ship To */}
                      <div style={{ flex: 1, padding: '13px 16px', borderRight: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Ship To</div>
                        <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '3px' }}>{editForm.customer_name || '—'}</div>
                        <div style={{ color: '#374151', marginBottom: '3px' }}>📞 {editForm.customer_phone || '—'}</div>
                        <div style={{ color: '#374151', lineHeight: '1.5' }}>📍 {editForm.shipping_address || '—'}</div>
                        {editForm.notes && (
                          <div style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>Note: {editForm.notes}</div>
                        )}
                      </div>
                      {/* Order Info */}
                      <div style={{ width: '210px', flexShrink: 0, padding: '13px 16px', backgroundColor: '#f9fafb' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Order Info</div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <tbody>
                            {([
                              ['Invoice', order.order_number],
                              ['Date', orderDate],
                              ['Status', editForm.status.charAt(0).toUpperCase() + editForm.status.slice(1)],
                              ...(order.steadfast_tracking_code ? [['Tracking', order.steadfast_tracking_code]] : []),
                              ...(order.payment_method ? [['Payment', order.payment_method === 'cash_on_delivery' ? 'COD' : order.payment_method]] : []),
                            ] as [string, string][]).map(([k, v]) => (
                              <tr key={k}>
                                <td style={{ padding: '3px 0', color: '#6b7280', fontWeight: '600', paddingRight: '8px', whiteSpace: 'nowrap' }}>{k}</td>
                                <td style={{ padding: '3px 0', fontWeight: '700', wordBreak: 'break-all' }}>{v}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Items Table */}
                    <div style={{ padding: '0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f3f4f6' }}>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>#</th>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Product</th>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Variants</th>
                            <th style={{ padding: '9px 14px', textAlign: 'center', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Qty</th>
                            <th style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Unit Price</th>
                            <th style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', color: '#9ca3af' }}>No items</td></tr>
                          ) : items.map((item: any, idx: number) => {
                            const color = item.selected_color || item.color || '';
                            const size = item.selected_size || item.size || '';
                            const weight = item.selected_weight || item.weight || '';
                            return (
                              <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                                <td style={{ padding: '9px 14px', color: '#6b7280' }}>{idx + 1}</td>
                                <td style={{ padding: '9px 14px', fontWeight: '600' }}>{item.product_name || item.name || 'Product'}</td>
                                <td style={{ padding: '9px 14px', color: '#64748b', fontSize: '12px' }}>
                                  {(color || size || weight) ? (
                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                      {color && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
                                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: color.toLowerCase() }} />
                                          {color}
                                        </span>
                                      )}
                                      {size && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
                                          {size}
                                        </span>
                                      )}
                                      {weight && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
                                          {weight}
                                        </span>
                                      )}
                                    </div>
                                  ) : '—'}
                                </td>
                                <td style={{ padding: '9px 14px', textAlign: 'center' }}>{item.quantity}</td>
                                <td style={{ padding: '9px 14px', textAlign: 'right' }}>৳{parseFloat(item.price || 0).toFixed(2)}</td>
                                <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '600' }}>৳{parseFloat(item.subtotal || 0).toFixed(2)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Totals */}
                    <div style={{ borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                      <table style={{ width: '260px', borderCollapse: 'collapse', fontSize: '13px', margin: '0' }}>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px 16px', color: '#6b7280' }}>Items Subtotal</td>
                            <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '600' }}>৳{itemsTotal.toFixed(2)}</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px 16px', color: '#6b7280' }}>Shipping</td>
                            <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '600' }}>৳{shippingCost.toFixed(2)}</td>
                          </tr>
                          {discount > 0 && (
                            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '8px 16px', color: '#16a34a' }}>Discount</td>
                              <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>-৳{discount.toFixed(2)}</td>
                            </tr>
                          )}
                          <tr style={{ backgroundColor: '#111827' }}>
                            <td style={{ padding: '10px 16px', color: 'white', fontWeight: '800', fontSize: '14px' }}>COD Amount</td>
                            <td style={{ padding: '10px 16px', textAlign: 'right', color: 'white', fontWeight: '800', fontSize: '15px' }}>৳{codAmount.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Footer note */}
                    <div style={{ padding: '10px 16px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
                      This is a system-generated packing slip for Steadfast Courier. Please attach to the parcel before dispatch.
                    </div>
                  </div>
                  </div>
                  )} {/* end slip tab */}

                  {/* ── Customer Fraud Check (Cross-Merchant) ── */}
                  {editModalTab === 'fraud' && (
                    <div style={{ marginTop: isMobile ? '0' : '20px' }}>
                      <CustomerIntelligenceWidget
                        customerHistory={customerHistory}
                        loading={customerHistoryLoading}
                        error={customerHistoryError}
                        phone={order?.customer_phone}
                        onRefresh={() => order?.customer_phone && loadCustomerHistory(order.customer_phone)}
                        currentOrderNumber={order?.order_number}
                        showStoreOrdersList={true}
                      />
                    </div>
                  )}
                </div>
              )} {/* end RIGHT panel */}
              </div>

              {/* ── Footer ── */}
              <div style={{
                padding: isMobile ? '12px 16px' : '12px 20px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: isMobile ? 'stretch' : 'flex-end',
                gap: '10px',
                backgroundColor: '#f8fafc', flexShrink: 0
              }}>
                <button onClick={onClose} disabled={editSaving} style={{
                  flex: isMobile ? 1 : undefined,
                  padding: '9px 20px', backgroundColor: '#e2e8f0',
                  color: '#475569', border: 'none', borderRadius: '8px',
                  fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s'
                }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#cbd5e1'; e.currentTarget.style.color = '#1e293b'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; }}
                >Cancel</button>
                <button onClick={handleSaveEdit} disabled={editSaving} style={{
                  flex: isMobile ? 2 : undefined,
                  padding: '9px 24px',
                  background: editSaving ? '#93c5fd' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontSize: '0.85rem', fontWeight: '600',
                  cursor: editSaving ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  boxShadow: editSaving ? 'none' : '0 4px 12px rgba(37, 99, 235, 0.2)',
                  transition: 'all 0.2s'
                }}>
                  {editSaving ? <>Saving...</> : <>Save Changes</>}
                </button>
              </div>
            </div>
          </div>

  );
};
